import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useMotionValue, useScroll, useSpring, useTransform } from "framer-motion";
import { ArrowUpRight, ArrowDown } from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";
import { scrollToId } from "./SmoothScroll";
import { FlagRow } from "./Flags";

const EASE = [0.16, 1, 0.3, 1];

const LogoFrame = () => {
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [10, -10]), { stiffness: 120, damping: 18 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-12, 12]), { stiffness: 120, damping: 18 });
  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };
  const reset = () => { mx.set(0); my.set(0); };
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.92 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 1.4, delay: 0.5, ease: EASE }}
      className="relative mx-auto w-full max-w-[400px] sm:max-w-[440px] lg:max-w-[480px] [perspective:1200px]"
      onMouseMove={onMove}
      onMouseLeave={reset}
      data-testid="hero-logo-frame"
    >
      <div className="absolute -inset-10 rounded-full bg-violet-600/30 blur-[90px]" />
      <motion.div style={{ rotateX: rx, rotateY: ry }} className="relative [transform-style:preserve-3d]">
        <div className="arch-frame relative aspect-square overflow-hidden border border-white/15 bg-[#050713]">
          <img src="/img/aura-agency-square.jpg" alt="Agenzia Aura_ - creator da Svizzera, Italia, Germania, Austria e Liechtenstein" className="h-full w-full object-cover" data-testid="hero-agency-photo" />
          <div className="absolute inset-0 shine" />
        </div>
        <div className="absolute -bottom-5 -left-4 md:-left-10 glass rounded-2xl px-4 py-3 [transform:translateZ(60px)]">
          <FlagRow className="gap-2.5 [&_.flag]:w-7 [&_.flag]:h-5 [&_.flag-ch]:w-5 [&_span.font-mono]:text-sky-300" testid="hero-flag-row" />
        </div>
        <div className="absolute -top-4 -right-3 md:-right-6 flex w-14 h-14 rounded-xl overflow-hidden ring-1 ring-white/25 shadow-[0_0_40px_rgba(0,146,70,0.45)] [transform:translateZ(80px)]" role="img" aria-label="Italia" data-testid="hero-italy-flag">
          <span className="flex-1 bg-[#009246]" /><span className="flex-1 bg-white" /><span className="flex-1 bg-[#ce2b37]" />
        </div>
      </motion.div>
    </motion.div>
  );
};

export const Hero = () => {
  const { t, lang } = useLang();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "25%"]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "-18%"]);

  return (
    <section ref={ref} id="top" className="relative min-h-[100svh] overflow-hidden pt-[72px]" data-testid="hero-section">
      <motion.div style={{ y: bgY }} className="absolute inset-0 -z-10">
        <img src="/img/aura-agency-square.jpg" alt="" className="h-[120%] w-full object-cover opacity-20 blur-lg scale-110" />
        <div className="absolute inset-0 bg-[#050713]/60" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_70%_30%,rgba(139,92,246,0.28),transparent_60%)]" />
      </motion.div>
      <div className="stars absolute inset-0 -z-10" />

      <div className="mx-auto max-w-7xl px-5 md:px-10 grid lg:grid-cols-[1.35fr_1fr] gap-14 lg:gap-10 items-center min-h-[calc(100svh-72px)] py-14">
        <motion.div style={{ y: textY }} key={lang}>
          <motion.p initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease: EASE }}
            className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-3 py-1.5 font-mono text-[11px] uppercase tracking-[0.25em] text-slate-200" data-testid="hero-badge">
            <span className="w-1.5 h-1.5 rounded-full bg-[#f43f5e] animate-pulse" /> {t.hero.badge}
          </motion.p>
          <h1 className="mt-8 font-hero font-extrabold uppercase leading-[1.04] tracking-[-0.01em] text-[1.9rem] sm:text-[2.6rem] lg:text-[3.3rem] xl:text-[3.7rem] text-white" data-testid="hero-title">
            {t.hero.lines.map((line, i) => (
              <span key={i} className="block overflow-hidden pb-[0.1em]">
                <motion.span className={`block ${i === 2 ? "text-chrome" : i === 1 ? "text-slate-200 font-bold text-[0.72em] tracking-[0.02em]" : ""}`}
                  initial={{ y: "110%" }} animate={{ y: "0%" }} transition={{ duration: 1.1, delay: 0.15 + i * 0.13, ease: EASE }}>
                  {line}
                </motion.span>
              </span>
            ))}
          </h1>
          <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.7, ease: EASE }}
            className="mt-8 max-w-xl text-base md:text-lg text-slate-300 leading-relaxed" data-testid="hero-subtitle">
            {t.hero.sub}
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.85, ease: EASE }} className="mt-10 flex flex-wrap gap-3">
            <button onClick={() => scrollToId("contact")} data-testid="hero-join-button" className="btn-chrome group rounded-full pl-6 pr-2 py-2 flex items-center gap-3 font-semibold text-[#050713]">
              {t.hero.ctaJoin}
              <span className="grid place-items-center w-9 h-9 rounded-full bg-[#050713] text-white transition-transform duration-300 group-hover:rotate-45"><ArrowUpRight className="w-4 h-4" /></span>
            </button>
            <Link to="/area-membri" data-testid="hero-members-button" className="rounded-full border border-white/20 px-6 py-3 font-semibold text-white hover:bg-white/10 transition-colors">
              {t.hero.ctaMembers}
            </Link>
          </motion.div>
          <motion.ul initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1, delay: 1.1 }} className="mt-12 flex flex-wrap gap-x-6 gap-y-2">
            {t.hero.chips.map((c) => (
              <li key={c} className="font-mono text-[11px] uppercase tracking-[0.18em] text-slate-300 flex items-center gap-2">
                <span className="w-1 h-1 rounded-full bg-sky-300" /> {c}
              </li>
            ))}
          </motion.ul>
        </motion.div>
        <LogoFrame />
      </div>
      <button onClick={() => scrollToId("about")} data-testid="hero-scroll-indicator" className="hidden md:flex absolute bottom-8 left-1/2 -translate-x-1/2 flex-col items-center gap-2 font-mono text-[11px] uppercase tracking-[0.3em] text-slate-300">
        {t.hero.scroll} <ArrowDown className="w-4 h-4 animate-bounce" />
      </button>
    </section>
  );
};
